/**
 * vm-pdp.js (L4, v5.3): PDP behaviour around Horizon's product form. DESIGN-SYSTEM §4.9.
 * <vm-pdp> (rendered once by blocks/vm-product-title) wires its own section:
 * 1. Size: nothing is preselected (patch 12 renders no checked size). The shopper's pick is kept
 *    here and re-applied after each Horizon re-render (product select promise).
 * 2. Add to cart is always enabled: with no size chosen, a click or submit (main button or the
 *    phone bar, which "puppets" the main button) is stopped before Horizon sees it (window capture
 *    runs before Horizon's document-capture delegation): "Choose a size." (role=alert), error
 *    border, the size group scrolled under the header, focus on the first size. Nothing is added.
 * 3. Phone bar state line: "Black · Choose a size" / "Black · M" / "Wine · $35".
 * 4. Desktop sticky buy box: --vm-buy-h from a ResizeObserver (CSS: top = min(header, 100vh - box)).
 * 5. Phone gallery row: "n / N" counter (+ a polite "Image n of N" live line), progress hairline, jump to the plate; drawer "See it".
 * 6. ?size=M from the size guide arrives with that size chosen (POLISH G-C).
 * Never reads window.scrollY.
 */
import { StandardEvents } from '@shopify/events';

const ALERT =
  '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="6.5"/><path d="M8 4.5v4.2M8 10.8v.7"/></svg>';

class VmPdp extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || document;
    this.size = null;
    this.ac = new AbortController();
    const o = { signal: this.ac.signal };
    const guard = (e) => this.#guard(e);
    window.addEventListener('click', guard, { capture: true, signal: this.ac.signal });
    window.addEventListener('submit', guard, { capture: true, signal: this.ac.signal });
    this.section.addEventListener('change', (e) => this.#pre(e), { capture: true, signal: this.ac.signal });
    this.section.addEventListener('change', (e) => this.#change(e), o);
    this.section.addEventListener(StandardEvents.productSelect, (e) => e.promise?.then(() => this.#restore()).catch(() => {}), o);
    this.section.addEventListener('slideshow:select', (e) => this.#count(e), o);
    this.section.addEventListener('click', (e) => this.#jump(e), o);
    const box = this.section.querySelector('.product-details');
    if (box) {
      const set = () => box.style.setProperty('--vm-buy-h', `${box.offsetHeight}px`);
      set();
      if ('ResizeObserver' in window) (this.ro = new ResizeObserver(set)).observe(box);
    }
    const bar = this.section.querySelector('sticky-add-to-cart');
    if (bar) {
      this.mo = new MutationObserver(() => this.#state());
      this.mo.observe(bar, { childList: true, subtree: true });
    }
    this.#state();
    this.#fromUrl();
  }

  disconnectedCallback() {
    this.ac?.abort();
    this.ro?.disconnect();
    this.mo?.disconnect();
  }

  get #group() {
    return this.section.querySelector('fieldset[data-vm-opt="size"]');
  }

  #radios() {
    return [...(this.#group?.querySelectorAll('input[type="radio"]') || [])];
  }

  #change(e) {
    const r = e.target;
    if (!(r instanceof HTMLInputElement) || !this.#group?.contains(r)) return;
    this.size = r.value;
    this.#error(false);
    this.#state();
  }

  /**
   * Colour (or version) changed while no size, or a size this colour lacks, is chosen: Horizon would ask
   * for "Navy" alone, Shopify would fill in S, Navy / S does not exist, and the box would turn
   * "Unavailable" with no price. Runs before Horizon's listener: quietly checks a size that exists in
   * the new colour (patch 12e list) so the request resolves; the server re-renders sizes unchecked.
   */
  #pre(e) {
    const t = e.target;
    const g = this.#group;
    if (!(t instanceof HTMLInputElement) || t.type !== 'radio' || !g || g.contains(t)) return;
    const picker = t.closest('variant-picker');
    if (!picker?.contains(g)) return;
    let pairs;
    try {
      pairs = JSON.parse(picker.querySelector('[data-vm-avail]')?.textContent || 'null');
    } catch {
      return;
    }
    if (!Array.isArray(pairs)) return;
    const key = [...picker.querySelectorAll('fieldset[data-vm-opt]')]
      .filter((f) => f !== g)
      .map((f) => `${f.querySelector('input:checked')?.value ?? ''}|`)
      .join('');
    const sizes = pairs.filter((p) => p[0] === key).map((p) => p[1]);
    if (!sizes.length || (this.size && sizes.includes(this.size))) return;
    this.size = null;
    const r = this.#radios().find((x) => x.value === sizes[0]);
    if (r) {
      r.dataset.vmQuiet = '';
      r.checked = true;
    }
  }

  /** After Horizon morphs the picker (server never checks a size), put the shopper's size back. */
  #restore() {
    const radios = this.#radios();
    if (this.size) {
      const r = radios.find((x) => x.value === this.size && !x.disabled);
      if (r) {
        r.checked = true;
        r.dataset.currentChecked = 'true';
      } else this.size = null;
    }
    this.#state();
  }

  /**
   * POLISH G-C (R4-03): ?size=M (the size guide's "Select this size", with ?variant=) arrives with that size
   * chosen. When the server already rendered that variant, the size is checked quietly; otherwise it is
   * clicked so Horizon resolves the variant. The size param is then dropped from the address.
   */
  #fromUrl() {
    let url;
    try {
      url = new URL(location.href);
    } catch {
      return;
    }
    const want = url.searchParams.get('size');
    if (!want || !this.#group) return;
    const r = this.#radios().find((x) => x.value === want && !x.disabled);
    url.searchParams.delete('size');
    try {
      history.replaceState(history.state, '', url.toString());
    } catch {}
    if (!r) return;
    let sel = null;
    try {
      sel = JSON.parse(r.closest('variant-picker')?.querySelector('script[type="application/json"]')?.textContent || 'null');
    } catch {}
    if (sel && Array.isArray(sel.options) && sel.options.includes(want) && url.searchParams.get('variant') === String(sel.id)) {
      r.checked = true;
      r.dataset.currentChecked = 'true';
      this.size = want;
      this.#state();
    } else {
      customElements.whenDefined('variant-picker').then(() => r.click());
    }
  }

  #guard(e) {
    const t = /** @type {Element} */ (e.target);
    if (!t?.closest || !this.section.contains(t) || !this.#group || this.size) return;
    const form = t.closest('product-form-component form, product-form-component');
    const stickyBtn = t.closest('.sticky-add-to-cart__button');
    const mainBtn = t.closest('product-form-component [ref="addToCartButton"]');
    if (e.type === 'submit' ? !form : !(mainBtn || stickyBtn)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.#error(true);
  }

  #error(on) {
    const g = this.#group;
    if (!g) return;
    const msg = g.querySelector('[data-vm-size-error]');
    g.classList.toggle('is-error', on);
    if (msg) msg.innerHTML = on ? `${ALERT}<span>Choose a size.</span>` : '';
    if (!on) return;
    const first = this.#radios().find((r) => !r.disabled);
    g.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    first?.focus({ preventScroll: true });
  }

  /** Phone bar line under the short name. */
  #state() {
    const line = this.section.querySelector('sticky-add-to-cart .sticky-add-to-cart__variant');
    if (!line) return;
    const color = this.section.querySelector('fieldset[data-vm-opt="color"] input:checked')?.value;
    let text;
    if (this.#group) text = [color, this.size || 'Choose a size'].filter(Boolean).join(' · ');
    else {
      const price = this.section.querySelector('.product-details .price, .product-details [ref="priceContainer"]')?.textContent?.trim();
      text = [color, price].filter(Boolean).join(' · ');
    }
    if (text && line.textContent !== text) {
      line.textContent = text;
      line.style.removeProperty('display');
    }
  }

  #count(e) {
    const nav = this.section.querySelector('vm-gallery-nav');
    if (!nav || !e.detail) return;
    const n = Number(nav.dataset.count) || 1;
    const i = (e.detail.index || 0) + 1;
    const out = nav.querySelector('[data-vm-gnav-i]');
    if (out) out.textContent = String(i);
    const live = nav.querySelector('[data-vm-gnav-live]');
    if (live) live.textContent = `Image ${i} of ${n}`;
    nav.querySelector('.vm-gnav__fill')?.style.setProperty('--vm-prog', `${(i / n) * 100}%`);
  }

  #jump(e) {
    const t = /** @type {Element} */ (e.target);
    const jump = t.closest?.('[data-vm-plate-jump]');
    const see = t.closest?.('[data-vm-see-plate]');
    if (!jump && !see) return;
    const show = this.section.querySelector('media-gallery slideshow-component');
    const phone = show && getComputedStyle(show).display !== 'none';
    if (phone) {
      e.preventDefault();
      const idx = Number(jump?.dataset.vmPlateJump ?? this.section.querySelector('[data-vm-plate-jump]')?.dataset.vmPlateJump ?? 1);
      /** @type {any} */ (show).select?.(idx);
      if (see) show.scrollIntoView({ block: 'start' });
    }
  }
}

if (!customElements.get('vm-pdp')) customElements.define('vm-pdp', VmPdp);
